import { map } from '../lodash-min';
import { hasWindow } from '../utils';
import { EMPTY_COMMERCE } from './analyticsGDDL';
import { vehicleToCommerce } from './analyticsGDDLmapping';

const eventTypes = {
  pageImpression: 'page-impression',
  buttonClick: 'button-click',
  toolStart: 'tool-start',
  toolSubmit: 'tool-submit',
  videoStart: 'video_start',
  videoComplete: 'video_complete'
};

const mapVehicleToAnalyticsData = (vehicle) => {
  return {
    reference: vehicle.reference,
    identification: vehicle.identification,
    make: vehicle.makeKey,
    model: vehicle.modelKey,
    modelFamily: vehicle.modelFamilyKey,
    bodyType: vehicle.bodyKey,
    labels: vehicle.labels,
    category: vehicle.categoryKey,
    fuel: vehicle.fuelKey,
    transmission: vehicle.gearboxKey,
    kw: vehicle.kw,
    hp: vehicle.hp,
    mileage: vehicle.mileage,
    firstRegistrationYear: vehicle.firstRegistrationYear,
    firstRegistrationMonth: vehicle.firstRegistrationMonth,
    price: vehicle.price,
    monthlyPrice: vehicle.monthlyPrice,
    monthlyPriceType: vehicle.monthlyPriceType,
    doors: vehicle.doors,
    seats: vehicle.seats,
    vat: vehicle.vat,
    equipments: map(vehicle.equipments, (eq) => eq.key),
    exteriorColor: vehicle.exteriorColorKey,
    interiorColor: vehicle.interiorColorKey,
    interiorUpholstery: vehicle.interiorUpholsteryKey,
    co2: vehicle.co2,
    euroNorm: vehicle.euroNorm,
    location: vehicle.dealer ? vehicle.dealer.name : ''
  };
};

const resolvePageInfo = (context, window) => {
  return {
    language: context ? context.culture : undefined,
    url: window.location.href,
    name: context?.page
  };
};

const analyticsInitialize = () => {
  // console.log('initialize analytics Autralis');
  // TagManager.initialize(tagManagerArgs);
};

const analyticsEventSubmit = (tracker, event) => {
  if (hasWindow() && tracker) {
    try {
      console.log('event: ', event);
      tracker.push(event);
    } catch (err) {
      // console.log('Event did not track correctly, reason:', err);
    }
  }
};

const buttonCtaEventInitialize = (props, context) => {
  return {
    event: eventTypes.buttonClick,
    page: resolvePageInfo(context, window),
    info: {
      buttonType: 'button',
      buttonCategory: props.buttonCategory ? props.buttonCategory : 'undefined-link',
      buttonName: props.name,
      buttonText: props.buttonText,
      buttonTarget: props.target,
      buttonActionUrl: props.url,
      buttonActionSearch: props.search
    }
  };
};

const videoStartEventInitialize = (type, data, context) => {
  const generateEventObject = (type, data) => {
    const commerce = data.vehicle ? vehicleToCommerce(data.vehicle) : EMPTY_COMMERCE;
    const vehicle = data && data.vehicle ? mapVehicleToAnalyticsData(data.vehicle) : undefined;

    return {
      event: eventTypes.videoStart,
      page: resolvePageInfo(context, window),
      info: {
        videoName: data.videoName || '',
        videoPosition: data.videoPosition || ''
      },
      commerce: commerce,
      vehicle: vehicle
    };
  };

  try {
    return generateEventObject(type, data, context);
  } catch (error) {
    // in case something fails we don't want the website to halt but to display an error
    console.error(error);
    return {};
  }
};

const videoCompleteEventInitialize = (type, data, context) => {
  const generateEventObject = (type, data) => {
    const commerce = data.vehicle ? vehicleToCommerce(data.vehicle) : EMPTY_COMMERCE;
    const vehicle = data && data.vehicle ? mapVehicleToAnalyticsData(data.vehicle) : undefined;

    return {
      event: eventTypes.videoComplete,
      page: resolvePageInfo(context, window),
      info: {
        videoName: data.videoName || '',
        videoPosition: data.videoPosition || ''
      },
      commerce: commerce,
      vehicle: vehicle
    };
  };

  try {
    return generateEventObject(type, data, context);
  } catch (error) {
    // in case something fails we don't want the website to halt but to display an error
    console.error(error);
    return {};
  }
};

const toolStartEventInitialize = (type, data, context) => {
  const generateEventObject = (type, data) => {
    const commerce = data.vehicle ? vehicleToCommerce(data.vehicle) : EMPTY_COMMERCE;
    const vehicle = data && data.vehicle ? mapVehicleToAnalyticsData(data.vehicle) : undefined;

    return {
      event: eventTypes.toolStart,
      page: resolvePageInfo(context, window),
      info: {
        toolType: data.toolType || '',
        toolName: data.toolName || '',
        toolStep: data.toolStepStart || '',
        toolStepOption: '',
        toolStepNumber: data.toolStepNumber || '1',
        requestType: data.requestType || '',
        leadID: '',
        errorMessage: '',
        errorType: ''
      },
      commerce: commerce,
      vehicle: vehicle
    };
  };

  try {
    return generateEventObject(type, data, context);
  } catch (error) {
    // in case something fails we don't want the website to halt but to display an error
    console.error(error);
    return {};
  }
};

const toolSubmitEventInitialize = (type, data, context) => {
  const generateEventObject = (type, data) => {
    const additionalData = data?.additionalData ? data.additionalData : data; //sometimes it's in additionalData sometimes in data...
    const commerce = additionalData?.vehicle ? vehicleToCommerce(additionalData?.vehicle) : EMPTY_COMMERCE;
    const vehicle = data && data.vehicle ? mapVehicleToAnalyticsData(data.vehicle) : undefined;

    return {
      event: eventTypes.toolSubmit,
      page: resolvePageInfo(context, window),
      info: {
        toolType: additionalData?.toolType || '',
        toolName: additionalData?.toolName || '',
        toolStep: additionalData?.toolStepStart || '',
        toolStepOption: additionalData?.toolStepOption || '',
        toolStepNumber: additionalData.toolStepNumber || '1',
        requestType: additionalData.requestType || '',
        leadID: '',
        errorMessage: '',
        errorType: ''
      },
      commerce: commerce,
      vehicle: vehicle
    };
  };

  try {
    return generateEventObject(type, data, context);
  } catch (error) {
    // in case something fails we don't want the website to halt but to display an error
    console.error(error);
    return {};
  }
};

const pageImpressionEventInitialize = (page, data, context) => {
  let eventData = {};

  // Fix for backwords compatible
  let type = '';
  if (typeof page === 'string') {
    type = page;
  } else {
    type = page.type;
  }

  if (type === 'vehicle') eventData = { ...eventData, vehicle: mapVehicleToAnalyticsData(data) };

  return {
    event: eventTypes.pageImpression,
    page: resolvePageInfo(context, window),
    ...eventData
  };
};

export const initialize = () => {
  const tracker = hasWindow() ? window.dataLayer : undefined;
  const settings = {
    id: ''
  };

  return {
    name: 'GOOGLE_TAG_AUTRALIS',
    settings: settings,
    tracker: tracker,

    analyticsInitialize: (settings) => analyticsInitialize(settings),
    pageImpressionEventInitialize: (page, data, context, session) => pageImpressionEventInitialize(page, data, context, session),
    analyticsEventSubmit: (event) => analyticsEventSubmit(tracker, event),
    productDetailInitialize: () => {
      return { event: 'product-initialize' };
    },
    productImpressionInitialize: () => {
      return { event: 'product-impression' };
    },
    productClickInitialize: () => {
      return { event: 'product-click' };
    },
    buttonCtaEventInitialize: (props, context) => buttonCtaEventInitialize(props, context),
    videoStartEventInitialize: (type, data, context) => videoStartEventInitialize(type, data, context),
    videoCompleteEventInitialize: (type, data, context) => videoCompleteEventInitialize(type, data, context),
    toolStartEventInitialize: (type, data, context) => toolStartEventInitialize(type, data, context),
    toolSubmitEventInitialize: (type, data, context) => toolSubmitEventInitialize(type, data, context),
    toolCompleteEventInitialize: () => {
      return { event: 'tool-complete' };
    },
    toolErrorEventInitialize: () => {
      return { event: 'tool-error' };
    },
    searchQueryEventInitialize: () => {
      return { event: 'search-query' };
    },
    filterQueryEventInitialize: () => {
      return { event: 'search-query' };
    },
    searchResultclickEventInitialize: () => {
      return { event: 'search-resultclick' };
    },
    contentblockImpressionEventInitialize: () => {
      return { event: 'content-block-impression' };
    },
    notificationImpressionEventInitialize: () => {
      return { event: 'notification-impression' };
    },
    notificationClickEventInitialize: () => {
      return { event: 'notification-click' };
    },
    userLoginEventInitialize: () => {
      return { event: 'user-login' };
    },
    productAddToFavoriteInitialize: () => {
      return { event: 'product-add-to-favorite' };
    }
  };
};
